import axios from 'axios';
import { UserEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';

/** Extra narrowing/inclusion for pickers that select more than one customer. */
export interface CustomerSearchOptions {
	/** Narrow the search to one customer type. Does not restrict {@link ids}. */
	userDisplayNameId?: string;
	/**
	 * Customers to return regardless of the search term or the type filter. A multi-select has to
	 * render the customers already chosen, and those are routinely outside the current search —
	 * picked under a different type, or restored from a prefilled link.
	 */
	ids?: string[];
}

/**
 * Returns user entities (specifically only customers) where the searchTerm matches full name,
 * email or phone.
 *
 * Backed by the staff-only `UserEntity/customer-search` REST endpoint, which applies the same
 * record-level security and customer-only filtering the GraphQL query did, and performs the
 * Australian phone-format normalisation server-side.
 * @param searchTerm The input value to compare with user full name, email or phone.
 * @param id Optional user id to always include in the results.
 * @param take The maximum number of results to return.
 * @param options Optional type filter and always-include ids — see {@link CustomerSearchOptions}.
 */
export async function searchCustomersByNameEmailPhone(
	searchTerm: string,
	id?: string,
	take = 10,
	options?: CustomerSearchOptions,
): Promise<UserEntity[]> {
	try {
		const params = new URLSearchParams();
		params.append('searchTerm', searchTerm ?? '');
		params.append('take', `${take}`);
		if (id) {
			params.append('id', id);
		}
		if (options?.userDisplayNameId) {
			params.append('userDisplayNameId', options.userDisplayNameId);
		}
		if (options?.ids?.length) {
			params.append('ids', options.ids.join(','));
		}

		const response = await axios.get<UserEntity[]>(`/api/entity/UserEntity/customer-search?${params.toString()}`);

		return response.data ?? [];
	} catch (e) {
		console.error(e);
		alertToast('Something went wrong while fetching customers', 'error');
		return [];
	}
}

/**
 * The customer-search endpoint returns at most 50 explicit ids per call, and the ids travel in the
 * query string, so a long list is fetched in batches.
 */
const CUSTOMER_IDS_BATCH = 50;

/** Returns the customers with the given ids, fetched in batches the search endpoint accepts. */
export async function fetchCustomersByIds(ids: string[]): Promise<UserEntity[]> {
	const batches: string[][] = [];
	for (let i = 0; i < ids.length; i += CUSTOMER_IDS_BATCH) {
		batches.push(ids.slice(i, i + CUSTOMER_IDS_BATCH));
	}

	const results = await Promise.all(
		batches.map(batch => searchCustomersByNameEmailPhone('', undefined, CUSTOMER_IDS_BATCH, { ids: batch })),
	);
	return results.flat();
}
